<template>
  <v-select
    :model-value="modelValue"
    :items="filteredItems"
    :disabled="disabled"
    bg-color="white"
    menu-icon="icon-arrow-down-xs"
    :menu-props="{ maxHeight: '300' }"
    class="filter-menu"
    :class="{ 'filter-active': modelValue?.length }"
    :placeholder="$t('activityLogs.filters.types')"
    :data-testid="dataTestId"
    density="compact"
    variant="outlined"
    multiple
    rounded
    hide-details
    item-value="value"
    item-title="title"
    @update:model-value="$emit('update:modelValue', $event)"
    @update:menu="search = ''"
  >
    <template #selection="{ index }">
      <span v-if="index === 0" class="body2">{{ $t("activityLogs.filters.types") }}</span>
    </template>
    <template #label>
      <span v-if="modelValue?.length" class="filter-label">{{ modelValue.length }}</span>
    </template>
    <template #prepend-item>
      <cui-select-search-field
        v-model="search"
        :placeholder="$t('general.search')"
        :data-test-id="`${dataTestId}-search`"
      />
    </template>
    <template #item="{ item, props: itemProps }">
      <v-list-subheader v-if="item.raw.type === 'subheader'" :title="item.title" />
      <v-list-item v-else v-bind="itemProps" title="">
        <template #prepend="{ isActive }">
          <v-list-item-action start>
            <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
          </v-list-item-action>
        </template>

        <v-list-item-title>
          {{ $t(`activityLogs.filters.type.${item.title}`) }}
        </v-list-item-title>
      </v-list-item>
    </template>
  </v-select>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { CuiSelectSearchField, useCuiSelectSearch } from "@coronet/cui";
import { ActivityLogsType } from "@/constants/activity-logs";
import type { ActivityLogsTypeSelectItem } from "@/types/activity-logs";

const mdmTypes: ActivityLogsType[] = [
  ActivityLogsType.MDM_DEVICE,
  ActivityLogsType.MDM_ACTION,
  ActivityLogsType.MDM_DEVICE_ACTION,
  ActivityLogsType.MDM_SYSTEM,
];

// TODO: investigate WHY these activity-log types are hidden from the Types filter. Historically only
// ActivityLogsPage excluded them; the export modals excluded a different set (or nothing), which was
// almost certainly unintentional. Baked in here so every caller is consistent — confirm whether these
// types should be hidden everywhere (and if so, document the reason) or not hidden at all.
const excludedTypes: ActivityLogsType[] = [
  ActivityLogsType.SIEM_CONFIGS,
  ActivityLogsType.THIRD_PARTY_APPS,
];

/**
 * Activity-log "Types" multi-select with an in-dropdown search.
 * Consolidates the three hand-rolled copies (ActivityLogsPage, ExportActivityLogsModal,
 * ScheduledExportModalFilters). Builds the item list from `ActivityLogsType` minus a fixed
 * exclusion list (`excludedTypes`), sorted alphabetically by translated label, with the MDM
 * section (subheader + MDM rows) pinned at the end.
 */
export default defineComponent({
  components: { CuiSelectSearchField },
  props: {
    /** Selected activity-log type values (v-model). */
    modelValue: {
      type: Array as PropType<ActivityLogsType[]>,
      default: () => [],
    },
    /** Disables the select (e.g. the export "everything" choice). */
    disabled: {
      type: Boolean,
      default: false,
    },
    /** `data-testid` for the select; the search field derives `${dataTestId}-search`. */
    dataTestId: {
      type: String,
      default: "activity-log-types-filter",
    },
  },
  emits: ["update:modelValue"],
  setup() {
    const { t } = useI18n();

    const label = (item: ActivityLogsTypeSelectItem) =>
      t(`activityLogs.filters.type.${item.title}`);

    const byLabel = (a: ActivityLogsTypeSelectItem, b: ActivityLogsTypeSelectItem) =>
      label(a).localeCompare(label(b));

    const allItems = computed<ActivityLogsTypeSelectItem[]>(() => {
      const toItem = (value: ActivityLogsType): ActivityLogsTypeSelectItem => ({
        title: value,
        value,
      });
      const values = Object.values(ActivityLogsType).filter(
        (value) => !excludedTypes.includes(value)
      );

      const nonMdm = values
        .filter((value) => !mdmTypes.includes(value))
        .map(toItem)
        .sort(byLabel);
      const mdm = values
        .filter((value) => mdmTypes.includes(value))
        .map(toItem)
        .sort(byLabel);

      // Keep the MDM section pinned at the very end, after the alphabetical list.
      if (mdm.length === 0) {
        return nonMdm;
      }
      return [...nonMdm, { type: "subheader", title: t("activityLogs.filters.type.mdm") }, ...mdm];
    });

    const isSubheader = (item: ActivityLogsTypeSelectItem) => item.type === "subheader";

    const { search, filteredItems } = useCuiSelectSearch<ActivityLogsTypeSelectItem>(
      () => allItems.value,
      label,
      { isSubheader }
    );

    return { search, filteredItems };
  },
});
</script>
